import React, { useState } from "react";
import { createPortal } from "react-dom";
import { AppWindow, Check, ChevronRight, Eye } from "lucide-react";
import { ARTCRAFT_APPS } from "./ArtCraftApp";
import { OPENABLE, VIEWER, extOf, openFileWith, saveDefault, savedDefault, suggestedFor } from "./openWith";

const BUILT_IN = { viewer: { ...VIEWER, icon: Eye }, word: { title: "Word", color: "text-blue-600" }, sheets: { title: "Excel", color: "text-green-600" } };
const info = (k) => ARTCRAFT_APPS[k] || BUILT_IN[k];
const Icon = ({ k }) => { const I = info(k)?.icon || AppWindow; return <I className={`w-4 h-4 ${info(k)?.color || ""}`} />; };

// "Open with ▸" inside a right-click menu: suggested programs, then "More programs…".
export const OpenWithItems = ({ node, onDone, onMore, prefix }) => {
  const [open, setOpen] = useState(false);
  const def = savedDefault(node.name);
  const picks = [...new Set([...(def && OPENABLE.includes(def) ? [def] : []), ...suggestedFor(node.name)])];
  return (
    <div>
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-slate-100 text-left" data-testid={`${prefix}-open-with`}>
        <AppWindow className="w-4 h-4 text-slate-500" /> Open with <ChevronRight className={`w-3.5 h-3.5 ml-auto transition-transform ${open ? "rotate-90" : ""}`} />
      </button>
      {open && (
        <div className="pl-4 border-l border-slate-200 ml-4" data-testid={`${prefix}-open-with-list`}>
          {picks.map((k) => (
            <button key={k} type="button" onClick={() => { onDone(); openFileWith(k, node); }} className="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-slate-100 text-left" data-testid={`${prefix}-open-with-${k}`}>
              <Icon k={k} /> {info(k).title}{def === k && <Check className="w-3.5 h-3.5 ml-auto text-emerald-600" />}
            </button>
          ))}
          <button type="button" onClick={() => { onDone(); onMore(node); }} className="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-slate-100 text-left text-sky-700" data-testid={`${prefix}-open-with-more`}>
            <AppWindow className="w-4 h-4" /> More programs…
          </button>
        </div>
      )}
    </div>
  );
};

// Every installed program; suggested ones first. "Always use" makes it the double-click program.
export const OpenWithDialog = ({ node, onClose }) => {
  const picks = suggestedFor(node.name);
  // Word and Excel only read their own formats, so they're offered only when suggested.
  const rest = OPENABLE.filter((k) => !picks.includes(k) && !["word", "sheets"].includes(k));
  const [sel, setSel] = useState(savedDefault(node.name) || picks[0] || "viewer");
  const [always, setAlways] = useState(false);
  const ext = extOf(node.name);
  const row = (k) => (
    <button key={k} type="button" onClick={() => setSel(k)} onDoubleClick={() => { setSel(k); go(k); }}
      className={`w-full flex items-center gap-2 px-3 py-2 rounded text-sm text-left ${sel === k ? "bg-sky-100 ring-1 ring-sky-400" : "hover:bg-slate-100"}`} data-testid={`open-with-dialog-app-${k}`}>
      <Icon k={k} /> {info(k).title}
    </button>
  );
  const go = (k = sel) => {
    if (always && ext) saveDefault(node.name, k);
    onClose();
    openFileWith(k, node);
  };
  return createPortal(
    <div className="fixed inset-0 z-[9999] bg-black/40 grid place-items-center p-3" onClick={onClose} data-testid="open-with-dialog">
      <div className="w-full max-w-sm rounded-lg bg-white text-slate-800 shadow-2xl p-4 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div>
          <div className="font-semibold">How do you want to open this file?</div>
          <div className="text-xs text-slate-500 truncate">{node.name}</div>
        </div>
        <div className="max-h-[50vh] overflow-y-auto space-y-0.5">
          {picks.length > 0 && <p className="text-[10px] uppercase tracking-wider text-slate-400 px-1">Suggested</p>}
          {picks.map(row)}
          <p className="text-[10px] uppercase tracking-wider text-slate-400 px-1 pt-2">Other programs</p>
          {rest.map(row)}
        </div>
        {ext && (
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={always} onChange={(e) => setAlways(e.target.checked)} data-testid="open-with-always" />
            Always use this program to open .{ext} files
          </label>
        )}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-1.5 rounded border border-slate-300 text-sm" data-testid="open-with-cancel">Cancel</button>
          <button type="button" onClick={() => go()} className="px-3 py-1.5 rounded bg-sky-600 text-white text-sm font-semibold" data-testid="open-with-ok">Open</button>
        </div>
      </div>
    </div>,
    document.body,
  );
};
